import {
    OrderTransaction,
    InsightQuote,
    IpoUnderwriters,
    Position,
    PositionWithExtraInfo,
    Currency,
    Sector,
} from '@orbis-systems/orbis-api-middleware';

/**
 * Force expand (simplification/normalization) of conditional and mapped types.
 * More info {@link https://github.com/microsoft/TypeScript/issues/47980 TypeScript/issues/47980}
 */
export type Expand<T> = T extends unknown ? { [K in keyof T]: Expand<T[K]> } : never;

export type Nullable<T> = T | null;

export interface Portfolio {
    totalGainPct: number;
    containsShort: boolean;
    homogeneous: boolean;
    total: number;
    totalGain: number;
    containsEquities: boolean;
    currency: string;
    containsOptions: boolean;
    totalCost: number;
    totalDaysValue: number;
    entries: Position[];
    securityTotalValue: number;
    realizedProfitLoss: {
        USD: number;
    };
}

export interface PortfolioWithExtraInfo extends Portfolio {
    entries: (PositionWithExtraInfo & { preciseValue?: number })[];
    totalCount?: number;
}

export interface FundScreenerDetails {
    asset: string;
    dividend_amount: number;
    dividend_percent: number;
    dividend_ex_date: string;
    dividend_pay_date: string;
    leverage: number;
    inverse: boolean;
    symbol: string;
    name: string;
    category: string;
    expense_ratio: number;
    last_price: number;
    price_change: number;
    price_change_percent: number;
    fund_type: string;
    market_cap: number;
    sectors: Sector[];
    sponsor: string;
    volume: number;
}

export type TopFundDetails = {
    quote: InsightQuote;
    symbol: string;
    last_price: number;
    closing_price: number;
    price_change: number;
    price_change_percent: number;
    name: string;
    inverse: boolean;
    sponsor_id: string;
    sponsor: string;
    asset_id: string;
    asset: string;
    category_id: string;
    category: string;
    structure_id: string;
    structure: string;
    levarage: number;
    expense_ratio: number;
    url: string;
    factsheet_url: string;
    prospectus_url: string;
    fund_type: string;
    sectors: Sector[];
};

export interface Ipo {
    symbol: string;
    country: string;
    name: string;
    file_date: string;
    ipo_date: string;
    ipo_date_approx: number;
    offer_price: number;
    offer_price_max: number;
    offer_price_min: number;
    offer_shares: number;
    first_day_open: number;
    first_day_close: number;
    day_one_return: number;
    status: string;
    prospectus_url: string;
    ceo: string;
    total_expenses: number;
    shares_over_alloted: number;
    shares_outstanding: number;
    lockup_period: number;
    lockup_expiration: string;
    quiet_period_expiration: string;
    cik: number;
    ipo_return: number;
    ipo_size: number;
    underwrites: IpoUnderwriters[];
    sector: {
        id: number;
        name: string;
        code: string;
    };
    industry_group: {
        id: number;
        name: string;
        code: string;
    };
    industry: {
        id: number;
        name: string;
        code: string;
    };
    sub_industry: {
        id: number;
        name: string;
        code: string;
    };
    exchange: {
        id: number;
        code: string;
        full_name: string;
    };
    quote?: InsightQuote;
}

export type StockScreenerNumberBasedCondition = {
    less_than?: number;
    more_than?: number;
};

export type StockScreenerDateBasedCondition = {
    after?: Date;
    before?: Date;
};

export type StockScreenerMatchingCondition = {
    include?: string[];
    exclude?: string[];
};

export type BondCoupon = {
    less_than?: number;
    more_than?: number;
};

export enum OrderType {
    Market = 'MARKET',
    Limit = 'LIMIT',
    Stop = 'STOP',
    StopLimit = 'STOP_LIMIT',
    Spread = 'Spread',
    Notional = 'Notional',
    Even = 'Even',
    Credit = 'Credit',
    Debit = 'Debit',
    TrailingStop = 'TRAILING_STOP',
    MarketOnClose = 'MARKET_ON_CLOSE',
    MarketOnOpen = 'MARKET_ON_OPEN',
    LimitOnClose = 'LIMIT_ON_CLOSE',
    LimitOnOpen = 'LIMIT_ON_OPEN',

    // TODO: Used in OV as OrderTypes, but they are algo types, should be handled differently in the future
    VWAP = 'VWAP',
    TWAP = 'TWAP',
    POV = 'POV',
}

export enum EquityTransaction {
    BuyToCover = OrderTransaction.BuyToCover,
    ShortSell = OrderTransaction.ShortSell,
    Sell = OrderTransaction.Sell,
    Buy = OrderTransaction.Buy,
    SellMargin = 'SELL_MARGIN',
    BuyMargin = 'BUY_MARGIN',
    SellCash = 'SELL_CASH',
    BuyCash = 'BUY_CASH',
}

export enum SettlementCurrency {
    USD = Currency.UsDollar,
    AUD = 'AUD',
    GBP = 'GBP',
    EUR = 'EUR',
    CAD = 'CAD',
}

export enum AlgoOrderType {
    VWAP = OrderType.VWAP,
    TWAP = OrderType.TWAP,
    POV = OrderType.POV,
}

export enum USCountryCode {
    US = 'US', // returned by midas API
    USA = 'USA', // returned by watchman API
}

/**
 * Interface representing permissions for a specific entity within a microservice.
 * Keys are action names (e.g., 'read', 'create', 'update', 'delete'), values are booleans.
 */
interface EntityPermissions {
    [action: string]: boolean;
}

/**
 * Interface representing a Microservice definition and its associated entity permissions.
 */
interface Microservice {
    id: number;
    slug: string;
    name: string;
    logo_url: string;
    is_visible_on_admin_sidebar: boolean;
    is_active: boolean;
    /**
     * A record where keys are entity names (e.g., 'users', 'contracts')
     * and values are objects defining permissions for that entity.
     */
    entities: Record<string, EntityPermissions>;
}

/**
 * Interface representing a Branch associated with a Watchmen instance or User.
 */
interface Branch {
    id: number;
    slug: string;
    code: string; // Note: code is a string "10003"
    name: string;
    firm_id: number;
}

/**
 * Interface representing a Watchmen instance linked to a user.
 */
interface WatchmenInstance {
    id: number;
    name: string;
    slug: string;
    url: string;
    created_at: string; // ISO date string
    updated_at: string; // ISO date string
}

/**
 * Interface representing the link between a User and a Watchmen instance/branch.
 */
export interface WatchmenUserLink {
    id: number;
    watchmen_user_id: number;
    watchmen_instance_id: number;
    branch_id: number;
    created_at: string; // ISO date string
    updated_at: string; // ISO date string
    watchmen_instance: WatchmenInstance;
    branch: Branch;
}

/**
 * Interface representing the User object.
 */
export interface UmaUser {
    id: number;
    first_name: string;
    last_name: string;
    username: string;
    email: string;
    phone: string; // Can be empty
    email_verified: boolean;
    phone_verified: boolean;
    mfa_enabled: boolean;
    do_not_ask_mfa_again: boolean;
    should_reset_password: boolean;
    rep_code: string; // Can be empty
    locale_id: number;
    locale: string | null; // Can be null
    initiator_id: number;
    allow_feedback: boolean;
    big_avatar_url: string; // Can be empty
    small_avatar_url: string; // Can be empty
    created_at: string; // ISO date string
    updated_at: string; // ISO date string
    watchmen_user_links: WatchmenUserLink[];
    theme: string; // Can be empty
    mode: string; // Can be empty
    is_chief_admin: boolean;
    admin_of_firm_ids: number[] | null; // Can be null or array of numbers
    microservices: Microservice[];
}

/**
 * Interface representing the authentication tokens.
 */
interface Tokens {
    access_token: string;
    refresh_token: string;
    pair_id: string;
    access_expires_at: number; // Unix timestamp
    refresh_expires_at: number; // Unix timestamp
}

/**
 * Interface representing the nested 'login_basic' object.
 */
interface LoginBasic {
    tokens: Tokens;
    user: UmaUser;
}

/**
 * Interface representing the entire JSON response structure.
 */
export interface UmaAuthResponse {
    status: number; // HTTP status code (e.g., 201)
    login_basic: LoginBasic;
}

export interface UmaB2bCurrentResponse {
    status: number;
    current_user: UmaUser;
}

/**
 * Interface representing the UMA Watchmen authentication tokens response.
 */
export interface UmaWatchmenAuthenticationTokensResponse {
    status: number;
    data: {
        token: string;
        expires_at: number;
    };
}

/**
 * Interface representing the Watchmen SSO platform response.
 */
export interface WatchmenSsoPlatformResponse {
    sessionId: string;
    stage: 'ESTABLISHED' | string;
}

export enum AccountCurrencyApproach {
    PerCurrency = 'PerCurrency',
    Equivalent = 'Equivalent',
}
